import React, { Dispatch, ReactElement, SetStateAction } from 'react';
import { Prompt } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';

interface CancelModalProps {
  title: string;
  description: string | ReactElement;
  size?: string;
  showCancellationModal: boolean;
  preventNavigation: boolean;
  cancelButtonText?: string;
  confirmButtonText?: string;
  setOpen: (value: React.SetStateAction<boolean>) => void;
  setNextLocation: Dispatch<SetStateAction<{ pathname: string }>>;
  onConfirm: () => void;
  onCancel: () => void;
  onClose?: () => void;
  setShowCancellationModal: (value: React.SetStateAction<boolean>) => void;
  'data-testid'?: string;
}

// TODO - upgrade to react router v6 will remove Prompt component

export const CancelModal = ({
  title,
  description,
  showCancellationModal,
  setShowCancellationModal,
  preventNavigation,
  setOpen,
  setNextLocation,
  onConfirm,
  onCancel,
  onClose,
  cancelButtonText = 'Leave',
  confirmButtonText = 'Stay',
  'data-testid': dataTestId,
}: CancelModalProps) => (
  <>
    <Prompt
      when={preventNavigation}
      message={(nextLocation, _action) => {
        if (preventNavigation) {
          setShowCancellationModal(true);
          setNextLocation(nextLocation);
          return false;
        }
        return true;
      }}
    />
    <Dialog
      open={showCancellationModal}
      onClose={() => setOpen(false)}
      aria-labelledby="cancel-modal-title"
      aria-describedby="cancel-modal-description"
      data-testid={dataTestId}
    >
      <DialogTitle component="div">
        <Typography variant="h6" component="h2" id="cancel-modal-title">
          {title}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose ?? onCancel}
          size="small"
          color="inherit"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {typeof description === 'string' ? (
          <DialogContentText id="cancel-modal-description">
            {description}
          </DialogContentText>
        ) : (
          <Box id="cancel-modal-description">{description}</Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          data-testid={dataTestId ? `${dataTestId}-cancel` : undefined}
        >
          {cancelButtonText}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          autoFocus
          data-testid={dataTestId ? `${dataTestId}-confirm` : undefined}
        >
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  </>
);
